import Joi from 'joi'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { toast } from 'react-toastify'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { joiResolver } from '@hookform/resolvers/joi'

import Btn from '../../../../UI/Btn'
import Icon from '../../../../UI/Icon'
import SelectField from '../../../../UI/SelectField'
import BaseModal from '../../../../UI/BaseModal/BaseModal'

import http from '../../../../shared/http'
import { TApi } from '../../../../shared/const'
import { schemaOptions } from '../../../../shared/i18n'
import { askConfirm } from '../../../../shared/confirmDialog'
import { types } from '../../../../shared/eventEmitter'

// Dasselbe Fenster wie beim Zusammenführen von Schulen — es tut dasselbe, nur
// mit Konten statt Schulen.
import styles from '../mergeDialog.module.scss'

const schema = Joi.object({
	teacher: Joi.object({
		id: Joi.number().integer().required()
	})
		.unknown()
		.required()
})

// So viele Junior Companies werden namentlich aufgezählt; der Rest steht als
// Zahl darunter. Zwölf Namen wären eine Bildschirmhöhe.
const SHOWN_COMPANIES = 6

const nameOf = teacher =>
	[teacher?.titlePrefixed, teacher?.firstName, teacher?.lastName, teacher?.titleSuffixed]
		.filter(Boolean)
		.join(' ')
		.trim()

function TeacherCard(props) {
	const { t } = useTranslation()
	const { teacher } = props

	return (
		<div className={clsx(styles.card, props.tone === 'from' ? styles.cardFrom : styles.cardTo)}>
			<div className={styles.cardHead}>
				<span className={styles.cardName}>{nameOf(teacher)}</span>
				<span className={clsx(styles.badge, teacher.verifiedTeacher ? styles.badgeVerified : styles.badgeOpen)}>
					{teacher.verifiedTeacher ? t('Verified') : t('Not Verified')}
				</span>
			</div>
			<p className={styles.cardLine}>{teacher.School?.name || t('No school assigned')}</p>
			{teacher.email && <p className={styles.cardLine}>{teacher.email}</p>}
			{props.children}
		</div>
	)
}

TeacherCard.propTypes = {
	teacher: PropTypes.object.isRequired,
	tone: PropTypes.oneOf(['from', 'to']).isRequired,
	children: PropTypes.node
}

export default function MergeTeachersModal() {
	const { t } = useTranslation()
	const {
		reset,
		watch,
		control,
		handleSubmit,
		formState: { errors, dirtyFields }
	} = useForm({
		resolver: joiResolver(schema, {
			errors: {
				labels: false,
				language: localStorage.getItem('i18nextLng')
			},
			...schemaOptions
		})
	})

	const [isLoading, setLoading] = useState(false)
	const [onDone, setOnDone] = useState(null)
	const [fromTeacher, setFromTeacher] = useState({})
	const [companies, setCompanies] = useState(null)

	const toTeacher = watch('teacher')

	/*
	 * Welche Junior Companies hängen an dem Konto, das aufgelöst wird? Genau die
	 * werden umgehängt — ohne sie bleibt im Dunkeln, wie groß der Eingriff ist,
	 * und nachher lässt sich nicht mehr nachsehen. Scheitert die Abfrage, bleibt
	 * die Aufzählung weg statt eine Fehlermeldung über das Fenster zu legen.
	 */
	const loadCompanies = teacherId => {
		setCompanies(null)
		http.get(TApi.ADMIN_GET_COMPANIES, {
			disableToast: true,
			params: { limit: 100, offset: 0, filter: JSON.stringify({ teacher: [teacherId] }) }
		})
			.then(({ data }) => setCompanies(data.allCompanies || []))
			.catch(() => setCompanies(null))
	}

	const handleOpen = (teacher, done) => {
		setFromTeacher(teacher)
		setOnDone(() => done)
		reset({ teacher: null })
		if (teacher?.id) loadCompanies(teacher.id)
	}

	const loadTeacherOptions = async (search, loadedOptions) => {
		const {
			data: { rows, count }
		} = await http.get(TApi.ADMIN_GET_TEACHERS, {
			params: {
				search,
				limit: 30,
				except: fromTeacher.id,
				offset: loadedOptions.length
			}
		})

		return {
			options: rows.map(row => ({ ...row, value: row.id, label: nameOf(row) })),
			hasMore: loadedOptions.length < count
		}
	}

	const onSubmit = async (fields, closeModal) => {
		// Der Vorgang löscht ein Konto und lässt sich nicht zurücknehmen —
		// deshalb noch einmal im Klartext nachfragen, mit beiden Namen darin.
		const confirmed = await askConfirm({
			title: t('Merge teachers?'),
			text: t('mergeTeachersConfirmText', { from: nameOf(fromTeacher), to: nameOf(fields.teacher) }),
			confirmLabel: t('Merge teachers'),
			cancelLabel: t('Cancel'),
			tone: 'danger'
		})
		if (!confirmed) return

		setLoading(true)

		http.put(TApi.ADMIN_PUT_TEACHERS_MERGE, {
			toTeacherId: fields.teacher.id,
			fromTeacherId: fromTeacher.id
		})
			.then(() => {
				toast.success(t('mergeTeachersDone', { to: nameOf(fields.teacher) }))
				closeModal()
				reset({ teacher: null })
				if (onDone) onDone()
			})
			.finally(() => {
				setLoading(false)
			})
	}

	const renderCompanies = () => {
		if (companies === null) return null
		if (!companies.length) return <p className={styles.cardLine}>{t('Supervises no Junior Company')}</p>

		return (
			<>
				<p className={styles.cardLine}>{t('supervisedCompaniesNote', { total: companies.length })}</p>
				<ul className={styles.cardList}>
					{companies.slice(0, SHOWN_COMPANIES).map(company => (
						<li key={company.id} className={styles.cardListItem}>
							{company.name}
						</li>
					))}
					{companies.length > SHOWN_COMPANIES && (
						<li className={styles.cardListMore}>
							{t('+ {{total}} more', { total: companies.length - SHOWN_COMPANIES })}
						</li>
					)}
				</ul>
			</>
		)
	}

	return (
		<BaseModal
			isLoading={isLoading}
			onOpen={handleOpen}
			nameEvent={types.openMergeTeacherModal}
			isDirty={Boolean(Object.keys(dirtyFields).length)}
			title={t('Merge teachers')}
			maxWidth={560}
		>
			{({ closeModal, requestClose }) => (
				<form className={styles.root} onSubmit={handleSubmit(form => onSubmit(form, closeModal))}>
					<p className={styles.intro}>{t('mergeTeachersIntro')}</p>

					<span className={styles.stepLabel}>{t('This account is dissolved')}</span>
					<TeacherCard teacher={fromTeacher} tone="from">
						{renderCompanies()}
					</TeacherCard>

					<div className={styles.arrow}>
						<Icon className={styles.arrowIcon} name="mergeArrow" size={{ w: 24, h: 28 }} color="#22404D" />
						<span>{t('Supervised Junior Companies move along')}</span>
					</div>

					<SelectField
						async
						required
						label={t('This account remains')}
						isSearchable
						// Der Text kommt nicht von Joi: Dessen deutsche Meldungen kennen
						// den Fall "kein Eintrag gewaehlt" nicht.
						error={errors.teacher && { message: t('Please choose the account that remains') }}
						disabled={isLoading}
						control={control}
						name="teacher"
						className={styles.selectField}
						placeholder={t('Search teacher by name')}
						debounceTimeout={400}
						loadOptions={loadTeacherOptions}
						// Im aufgeklappten Verzeichnis steht die Schule dazu — bei
						// gleichnamigen Lehrkräften ist sie das Unterscheidungsmerkmal.
						formatOptionLabel={({ label, ...row }, { context }) =>
							context === 'value' ? (
								<span className={styles.cardName}>{label}</span>
							) : (
								<div className={styles.option}>
									<div className={styles.optionHead}>
										<span className={styles.cardName}>{label}</span>
										<span
											className={clsx(
												styles.badge,
												row.verifiedTeacher ? styles.badgeVerified : styles.badgeOpen
											)}
										>
											{row.verifiedTeacher ? t('Verified') : t('Not Verified')}
										</span>
									</div>
									<span className={styles.optionLine}>
										{row.School?.name || t('No school assigned')}
									</span>
								</div>
							)
						}
					/>

					{toTeacher?.id && <TeacherCard teacher={toTeacher} tone="to" />}

					<div className={styles.warning}>
						<Icon className={styles.warningIcon} name="danger" size={{ w: 18, h: 18 }} color="#EF8400" />
						<span>{t('mergeTeachersWarning')}</span>
					</div>

					<div className={styles.actions}>
						<Btn type="button" theme="secondary" disabled={isLoading} onClick={requestClose}>
							{t('Cancel')}
						</Btn>
						<Btn loading={isLoading} disabled={isLoading} type="submit">
							{t('Merge teachers')}
						</Btn>
					</div>
				</form>
			)}
		</BaseModal>
	)
}
